import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, Eye, FolderOpen, Upload } from 'lucide-react';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { useToast } from '../../../shared/components/Toast';
import { mensagemDeErro } from '../../pessoas/api';
import { CollaboratorDocument, visualizarMeuDocumento } from '../../pessoas/api/documentos';
import { DocumentStatusBadge } from '../../pessoas/components/DocumentosTab';
import { DOCUMENT_CATEGORY_LABELS, canReplaceDocument, formatDate } from '../../pessoas/components/format';
import { UploadDocumentModal } from '../../pessoas/components/UploadDocumentModal';
import { useEnviarMeuDocumento, useMeuChecklist, useMeusDocumentos, useMeusTipos } from '../../pessoas/hooks/cadastro';

/**
 * Portal do colaborador (US-GP-002, fluxo 13): checklist do vínculo,
 * envio, acompanhamento da validação e reenvio de documento rejeitado.
 * Documentos de uso interno não aparecem aqui (RN-GP-002-098).
 */
const MeusDocumentosPage: React.FC = () => {
  const toast = useToast();
  const documentos = useMeusDocumentos();
  const checklist = useMeuChecklist();
  const tipos = useMeusTipos();
  const enviar = useEnviarMeuDocumento();
  const [upload, setUpload] = useState<{ open: boolean; previous: CollaboratorDocument | null; presetTypeId: string | null }>(
    { open: false, previous: null, presetTypeId: null },
  );

  async function abrir(id: string) {
    try {
      await visualizarMeuDocumento(id);
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível abrir o documento.'));
    }
  }

  if (documentos.isLoading || checklist.isLoading) {
    return <div className="p-8 text-center text-gray-500">Carregando...</div>;
  }

  const itens = checklist.data ?? [];
  const docs = documentos.data ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <FolderOpen className="h-6 w-6 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Meus Documentos</h1>
        </div>
        <Button size="sm" onClick={() => setUpload({ open: true, previous: null, presetTypeId: null })}>
          <Upload className="mr-1 h-4 w-4" /> Enviar documento
        </Button>
      </div>

      {itens.length > 0 && (
        <section className="space-y-2 rounded-lg border border-gray-200 p-4">
          <h2 className="font-semibold text-gray-800">Documentos solicitados</h2>
          <ul className="space-y-2">
            {itens.map((item) => {
              const doc = docs.find((d) => d.id === item.document?.id);
              return (
                <li key={item.type.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="flex items-center gap-2">
                    {item.satisfied
                      ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                      : <AlertTriangle className="h-4 w-4 text-amber-500" />}
                    {item.type.name}
                    {doc && <DocumentStatusBadge status={doc.status} label={doc.statusLabel} />}
                  </span>
                  {!item.satisfied && item.type.collaboratorCanUpload && (!doc || canReplaceDocument(doc.status)) && (
                    <button
                      type="button"
                      className="text-sm font-medium text-blue-700 hover:underline"
                      onClick={() => setUpload({ open: true, previous: doc ?? null, presetTypeId: item.type.id })}
                    >
                      {doc ? 'Reenviar' : 'Enviar'}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className="space-y-2">
        <h2 className="font-semibold text-gray-800">Enviados</h2>
        {docs.length === 0 ? (
          <EmptyState size="compact" title="Nenhum documento enviado" />
        ) : (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {docs.map((doc) => (
              <li key={doc.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
                <div>
                  <p className="font-medium text-gray-900">
                    {doc.typeName} <span className="font-normal text-gray-500">· {DOCUMENT_CATEGORY_LABELS[doc.category ?? 'pessoal']}</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    v{doc.version} · enviado em {formatDate(doc.uploadedAt)}{doc.expiresAt ? ` · válido até ${formatDate(doc.expiresAt)}` : ''}
                  </p>
                  {doc.rejectionComment && (
                    <p className="text-xs text-red-700">
                      {doc.rejectionReasonLabel ? `${doc.rejectionReasonLabel}: ` : ''}{doc.rejectionComment}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <DocumentStatusBadge status={doc.status} label={doc.statusLabel} />
                  <button type="button" onClick={() => abrir(doc.id)} className="rounded p-1.5 text-gray-500 hover:bg-gray-100" aria-label="Visualizar">
                    <Eye className="h-4 w-4" />
                  </button>
                  {canReplaceDocument(doc.status) && (tipos.data ?? []).some((t) => t.id === doc.documentTypeId) && (
                    <Button size="sm" variant="secondary" onClick={() => setUpload({ open: true, previous: doc, presetTypeId: null })}>
                      Nova versão
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <UploadDocumentModal
        open={upload.open}
        mode="me"
        types={tipos.data ?? []}
        previous={upload.previous}
        presetTypeId={upload.presetTypeId}
        submitting={enviar.isPending}
        onClose={() => setUpload({ open: false, previous: null, presetTypeId: null })}
        onSubmit={async (payload) => {
          await enviar.mutateAsync(payload);
          setUpload({ open: false, previous: null, presetTypeId: null });
          toast.success('Documento enviado. O Departamento Pessoal vai conferir.');
        }}
      />
    </div>
  );
};

export default MeusDocumentosPage;
